import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { getCurrentEmpresaIdSync } from '../utils/empresaUtils';

export interface OperatingHours {
  open: string | null;
  close: string | null;
  last_booking: string | null;
  is_open: boolean;
}

export interface SchedulingSettings {
  id?: string;
  empresa_id: string;
  operating_hours: Record<string, OperatingHours>;
  slot_duration_minutes: number;
  slots_per_interval: number;
  buffer_time_minutes: number;
  min_lead_time_hours: number;
  max_booking_days_advance: number;
  contact_phone?: string;
  contact_whatsapp?: string;
  contact_email?: string;
  created_at?: string;
  updated_at?: string;
}

export interface Holiday {
  id?: string;
  empresa_id: string;
  holiday_date: string;
  name?: string;
  is_closed: boolean;
  custom_hours?: {
    open: string;
    close: string;
    last_booking: string;
  };
  created_at?: string;
}

export interface TimeSlot {
  time: string;
  available: boolean;
  availableCount: number;
  totalSlots: number;
}

interface SchedulingStore {
  settings: SchedulingSettings | null;
  holidays: Holiday[];
  isLoading: boolean;
  error: string | null;
  
  fetchSettings: (empresaId: string) => Promise<void>;
  updateSettings: (settings: Partial<SchedulingSettings>) => Promise<void>;
  
  fetchHolidays: (empresaId: string) => Promise<void>;
  addHoliday: (holiday: Omit<Holiday, 'id' | 'created_at'>) => Promise<void>;
  updateHoliday: (id: string, holiday: Partial<Holiday>) => Promise<void>;
  deleteHoliday: (id: string) => Promise<void>;
  
  calculateAvailability: (date: string) => Promise<TimeSlot[]>;
  clearError: () => void;
}

export const useSchedulingStore = create<SchedulingStore>((set, get) => ({
  settings: null,
  holidays: [],
  isLoading: false,
  error: null,

  fetchSettings: async (empresaId: string) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('scheduling_settings')
        .select('*')
        .eq('empresa_id', empresaId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        set({ settings: data as SchedulingSettings, isLoading: false });
      } else {
        // Create default settings if none exist
        const defaultSettings: Omit<SchedulingSettings, 'id' | 'created_at' | 'updated_at'> = {
          empresa_id: empresaId,
          operating_hours: {
            monday: { open: '08:00', close: '18:00', last_booking: '17:30', is_open: true },
            tuesday: { open: '08:00', close: '18:00', last_booking: '17:30', is_open: true },
            wednesday: { open: '08:00', close: '18:00', last_booking: '17:30', is_open: true },
            thursday: { open: '08:00', close: '18:00', last_booking: '17:30', is_open: true },
            friday: { open: '08:00', close: '18:00', last_booking: '17:30', is_open: true },
            saturday: { open: '09:00', close: '14:00', last_booking: '13:30', is_open: true },
            sunday: { open: null, close: null, last_booking: null, is_open: false },
          },
          slot_duration_minutes: 30,
          slots_per_interval: 5,
          buffer_time_minutes: 0,
          min_lead_time_hours: 2,
          max_booking_days_advance: 30,
        };

        const { data: newSettings, error: createError } = await supabase
          .from('scheduling_settings')
          .insert(defaultSettings)
          .select()
          .single();

        if (createError) throw createError;
        set({ settings: newSettings as SchedulingSettings, isLoading: false });
      }
    } catch (error: any) {
      console.error('Error fetching scheduling settings:', error);
      set({ error: error.message || 'Error loading scheduling settings', isLoading: false });
    }
  },

  updateSettings: async (updates: Partial<SchedulingSettings>) => {
    set({ isLoading: true, error: null });
    try {
      const empresaId = getCurrentEmpresaIdSync();
      if (!empresaId) {
        throw new Error('No empresa selected');
      }

      const { data, error } = await supabase
        .from('scheduling_settings')
        .upsert({
          empresa_id: empresaId,
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) throw error;
      set({ settings: data as SchedulingSettings, isLoading: false });
    } catch (error: any) {
      console.error('Error updating scheduling settings:', error);
      set({ error: error.message || 'Error updating settings', isLoading: false });
      throw error;
    }
  },

  fetchHolidays: async (empresaId: string) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('scheduling_holidays')
        .select('*')
        .eq('empresa_id', empresaId)
        .order('holiday_date', { ascending: true });

      if (error) throw error;
      set({ holidays: (data as Holiday[]) || [], isLoading: false });
    } catch (error: any) {
      console.error('Error fetching holidays:', error);
      set({ error: error.message || 'Error loading holidays', isLoading: false });
    }
  },

  addHoliday: async (holiday: Omit<Holiday, 'id' | 'created_at'>) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('scheduling_holidays')
        .insert(holiday)
        .select()
        .single();

      if (error) throw error;
      set(state => ({
        holidays: [...state.holidays, data as Holiday],
        isLoading: false,
      }));
    } catch (error: any) {
      console.error('Error adding holiday:', error);
      set({ error: error.message || 'Error adding holiday', isLoading: false });
      throw error;
    }
  },

  updateHoliday: async (id: string, updates: Partial<Holiday>) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('scheduling_holidays')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      set(state => ({
        holidays: state.holidays.map(h => h.id === id ? (data as Holiday) : h),
        isLoading: false,
      }));
    } catch (error: any) {
      console.error('Error updating holiday:', error);
      set({ error: error.message || 'Error updating holiday', isLoading: false });
      throw error;
    }
  },

  deleteHoliday: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const { error } = await supabase
        .from('scheduling_holidays')
        .delete()
        .eq('id', id);

      if (error) throw error;
      set(state => ({
        holidays: state.holidays.filter(h => h.id !== id),
        isLoading: false,
      }));
    } catch (error: any) {
      console.error('Error deleting holiday:', error);
      set({ error: error.message || 'Error deleting holiday', isLoading: false });
      throw error;
    }
  },

  calculateAvailability: async (date: string): Promise<TimeSlot[]> => {
    try {
      const empresaId = getCurrentEmpresaIdSync();
      if (!empresaId) {
        throw new Error('No empresa selected');
      }

      const settings = get().settings;
      if (!settings) {
        await get().fetchSettings(empresaId);
      }

      const finalSettings = get().settings;
      if (!finalSettings) {
        throw new Error('Scheduling settings not found');
      }

      // Get day of week
      const dateObj = new Date(date);
      const dayIndex = dateObj.getDay();
      const dayOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][dayIndex] as string;
      const dayHours = finalSettings.operating_hours[dayOfWeek];

      // Check if it's a holiday
      const holidays = get().holidays;
      const holiday = holidays.find(h => h.holiday_date === date);
      
      if (holiday?.is_closed) {
        return []; // No availability on closed holidays
      }

      // Use holiday custom hours if available, otherwise use day hours
      const hours = holiday?.custom_hours ? {
        open: holiday.custom_hours.open,
        close: holiday.custom_hours.close,
        last_booking: holiday.custom_hours.last_booking,
        is_open: true,
      } : dayHours;

      if (!hours || !hours.is_open || !hours.open || !hours.close) {
        return []; // Closed day
      }

      // Generate time slots
      const slots: TimeSlot[] = [];
      const [openHour, openMin] = hours.open.split(':').map(Number);
      const [closeHour, closeMin] = hours.close.split(':').map(Number);
      const lastBookingTime = hours.last_booking || hours.close;
      const [lastBookingHour, lastBookingMin] = lastBookingTime.split(':').map(Number);
      
      if (openHour === undefined || openMin === undefined || closeHour === undefined || closeMin === undefined || lastBookingHour === undefined || lastBookingMin === undefined) {
        return []; // Invalid time format
      }

      const slotDuration = finalSettings.slot_duration_minutes;
      const totalSlots = finalSettings.slots_per_interval;

      let currentHour = openHour;
      let currentMin = openMin;

      while (
        currentHour < lastBookingHour ||
        (currentHour === lastBookingHour && currentMin <= lastBookingMin)
      ) {
        const timeStr = `${String(currentHour).padStart(2, '0')}:${String(currentMin).padStart(2, '0')}`;
        
        // Count existing appointments for this time slot
        const { count } = await supabase
          .from('appointments')
          .select('*', { count: 'exact', head: true })
          .eq('empresa_id', empresaId)
          .eq('appointment_date', date)
          .eq('appointment_time', timeStr)
          .in('status', ['pending', 'confirmed']);

        const bookedCount = count || 0;
        const availableCount = Math.max(0, totalSlots - bookedCount);

        slots.push({
          time: timeStr,
          available: availableCount > 0,
          availableCount,
          totalSlots,
        });

        // Move to next slot
        currentMin += slotDuration;
        if (currentMin >= 60) {
          currentHour += Math.floor(currentMin / 60);
          currentMin = currentMin % 60;
        }
      }

      return slots;
    } catch (error: any) {
      console.error('Error calculating availability:', error);
      throw error;
    }
  },

  clearError: () => set({ error: null }),
}));
